import * as React from 'react';

/**
 * Krok FUNK1 bod 2 — JEDNA TABULKA SEZNAMU PRO CELY ADMIN.
 *
 * Nalez z pruchodu celou aplikaci (R.B. 4. 10. 2026): na 390 px se sloupce
 * `/smlouvy-sablony`, `/elektronicke-podpisy`, `/reporty`, `/audit`
 * a `/approvals` zmackly na jedno az dve pismena („S t á h n o u t  P D F"
 * svisle). `/zakazky`, `/klienti`, `/smlouvy` a `/fakturace` tenhle problem
 * nemaji — jejich tabulka ma tridu `rh-a2-tbl`, kterou media dotaz
 * v `styles/admin-v2.css` prekresli na karty pod sebou.
 *
 * Tahle komponenta je ten vzor jako KOMPONENTA, ne jako zkopirovane trídy:
 *  - hlavicka (`<th>`) i popisek karty na mobilu (`td[data-label]`) berou
 *    popisek sloupce ze STEJNEHO mista (`ListColumn.label`), takze se
 *    nemohou rozejit (TESTOVANI.md §8 pravidlo 2),
 *  - vzhled nekresli nic noveho: jen `rh-a2-tbl-wrap` + `rh-a2-tbl`, tedy
 *    presne to, co uz ma seznam zakazek (FUNK1 nemeni vzhled, zadani 06:20).
 *
 * Sloupec akci (`srOnlyLabel: true`) hlavicku nese jen pro ctecku a na
 * mobilu u nej popisek nestoji — tlacitko mluvi samo.
 */

export interface ListColumn<R> {
  /** Stabilni klic sloupce (poradi v DOM, `key` pro React). */
  readonly key: string;
  /** Popisek — hlavicka tabulky i popisek radku karty na mobilu. */
  readonly label: string;
  /** Sloupec akci: hlavicka jen pro ctecku, na mobilu bez popisku. */
  readonly srOnlyLabel?: boolean;
  readonly align?: 'right';
  readonly cell: (row: R) => React.ReactNode;
}

export interface ListTableProps<R> {
  readonly columns: readonly ListColumn<R>[];
  readonly rows: readonly R[];
  readonly rowKey: (row: R) => string;
  /** `data-testid` radku — obrazovky na nej mivaji navazane sady. */
  readonly rowTestId?: (row: R) => string;
  readonly testId?: string;
  /** Popis tabulky pro ctecku (`<caption class="rh-sr-only">`). */
  readonly caption?: string;
  /** Co ukazat, kdyz `rows` je prazdne (jeden radek pres vsechny sloupce). */
  readonly empty?: React.ReactNode;
  /**
   * Krok UXF2 bod 6: KLIKATELNY RADEK. Seznamy, ktere radkem otviraji panel
   * (poptavky), mely proto vlastni `<table>` — a s nim i vlastni (chybejici)
   * popisky karet na mobilu. Radek je `role="button"` s klavesnici, aby se
   * stejna obrazovka ovladala mysi i tabulatorem.
   */
  readonly onRowClick?: (row: R) => void;
}

export function ListTable<R>({
  columns,
  rows,
  rowKey,
  rowTestId,
  testId,
  caption,
  empty,
  onRowClick,
}: ListTableProps<R>): React.JSX.Element {
  return (
    <div className="rh-a2-tbl-wrap">
      <table className="rh-a2-tbl" {...(testId === undefined ? {} : { 'data-testid': testId })}>
        {caption === undefined ? null : <caption className="rh-sr-only">{caption}</caption>}
        <thead>
          <tr>
            {columns.map((column) => (
              <th
                key={column.key}
                scope="col"
                className={[
                  column.srOnlyLabel === true ? 'rh-sr-only' : '',
                  column.align === 'right' ? 'rh-a2-r' : '',
                ]
                  .filter((part) => part !== '')
                  .join(' ')}
              >
                {column.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && empty !== undefined ? (
            <tr>
              <td colSpan={columns.length}>{empty}</td>
            </tr>
          ) : (
            rows.map((row) => (
              <tr
                key={rowKey(row)}
                {...(rowTestId === undefined ? {} : { 'data-testid': rowTestId(row) })}
                {...(onRowClick === undefined
                  ? {}
                  : {
                      className: 'rh-a2-tbl__row--open',
                      role: 'button',
                      tabIndex: 0,
                      onClick: () => onRowClick(row),
                      onKeyDown: (event: React.KeyboardEvent<HTMLTableRowElement>) => {
                        if (event.key === 'Enter' || event.key === ' ') onRowClick(row);
                      },
                    })}
              >
                {columns.map((column) => (
                  <td
                    key={column.key}
                    className={column.align === 'right' ? 'rh-a2-r' : undefined}
                    /* Popisek karty na mobilu — TENTYZ retezec jako hlavicka. */
                    {...(column.srOnlyLabel === true ? {} : { 'data-label': column.label })}
                  >
                    {column.cell(row)}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}
